/**
 * Lokale app-innstillinger som lever på enheten (localStorage), ikke i databasen.
 *
 *  - Strømprisen som lys-strømoverslaget bruker. Personlig, svinger med spot +
 *    nettleie, og hører ikke hjemme per kasse — lagres globalt per nettleser.
 *  - Målt/anslått PPFD ved 100 % intensitet. Alle DLI-tall hviler på denne;
 *    standarden er et beregnet anslag fra Photone-målinger til du kalibrerer i
 *    innstillingene. Riggen er lik for alle kassene (samme lys, samme avstand), så
 *    én verdi holder.
 *
 * Lyset er 24V Samsung LM281B-barer i alle kasser. Appen hadde en velger for
 * den gamle 12V-stripa også, men den ble fjernet 23.09.2026: Markus holder seg
 * til 24V, og velgeren var bare en ekstra ting å bomme på.
 */
import { writable } from 'svelte/store';
import { STROMPRIS_KR_KWH } from './energi';

const STROMPRIS_KEY = 'plantepotte:strompris';
// Ny målerunde erstatter den eldre kalibreringen én gang per nettleser.
// Senere manuelle justeringer lagres og beholdes under denne nøkkelen.
const PPFD_KEY = 'plantepotte:ppfd:bar24:20260927';
const AVSTAND_KEY = 'plantepotte:lysavstander:v1';

/** Lyset over kassene: 24V Samsung LM281B-barer, to per kasse. */
export const LYS = {
  navn: '24V Samsung LM281B-barer',
  kortNavn: '24V-barer',
  /** Effekt (W) per kasse ved 100 % — 2 × 25 W. Driver strømoverslaget. */
  watt: 50,
  /**
   * Photone Full Spectrum + Red, begge barer på 100 %, 27.09.2026.
   * 224–258 PPFD ved 13,5 cm. 240 er et avrundet foreløpig anslag,
   * ikke et målt gjennomsnitt fra et komplett rutenett.
   */
  standardPpfd: 240,
} as const;

// Rester fra variant-tiden: valget og stripas kalibrering trengs ikke lenger.
try {
  localStorage.removeItem('plantepotte:lysvariant');
  localStorage.removeItem('plantepotte:ppfd');
} catch {
  /* privat modus e.l. */
}

/** Klamp til et rimelig område (0–20 kr/kWh) og avvis søppel. */
function gyldigPris(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v.replace(',', '.')) : Number(v);
  if (!Number.isFinite(n) || n <= 0 || n > 20) return null;
  return Math.round(n * 100) / 100;
}

/**
 * Gyldig PPFD-måling: 20–1500 µmol/m²/s. Under 20 er ikke et vekstlys, over
 * 1500 er sterkere enn sol i sør — begge er nesten sikkert tastefeil.
 */
function gyldigPpfd(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v.replace(',', '.')) : Number(v);
  if (!Number.isFinite(n) || n < 20 || n > 1500) return null;
  return Math.round(n);
}

function lesLagret<T>(key: string, valider: (v: unknown) => T | null, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return (raw !== null ? valider(raw) : null) ?? fallback;
  } catch {
    return fallback;
  }
}

export const strompris = writable<number>(lesLagret(STROMPRIS_KEY, gyldigPris, STROMPRIS_KR_KWH));

strompris.subscribe((v) => {
  try {
    localStorage.setItem(STROMPRIS_KEY, String(v));
  } catch {
    /* privat modus e.l. — kjør videre uten å lagre */
  }
});

/** Sett en ny strømpris hvis den er gyldig; returnerer true ved lagring. */
export function settStrompris(v: unknown): boolean {
  const p = gyldigPris(v);
  if (p === null) return false;
  strompris.set(p);
  return true;
}

/** Målt/antatt PPFD (µmol/m²/s) ved 100 % intensitet — grunnlaget for all DLI. */
export const ppfdMaks = writable<number>(lesLagret(PPFD_KEY, gyldigPpfd, LYS.standardPpfd));

ppfdMaks.subscribe((v) => {
  try {
    localStorage.setItem(PPFD_KEY, String(v));
  } catch {
    /* privat modus e.l. — kjør videre uten å lagre */
  }
});

/** Sett målt PPFD hvis verdien er gyldig; returnerer true ved lagring. */
export function settPpfdMaks(v: unknown): boolean {
  const p = gyldigPpfd(v);
  if (p === null) return false;
  ppfdMaks.set(p);
  return true;
}

function lesAvstander(): Record<string, number> {
  try {
    const data = JSON.parse(localStorage.getItem(AVSTAND_KEY) ?? '{}');
    if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
    const gyldige: Record<string, number> = {};
    for (const [id, v] of Object.entries(data)) {
      if (typeof v === 'number' && Number.isFinite(v) && v >= 10 && v <= 20) gyldige[id] = v;
    }
    return gyldige;
  } catch { return {}; }
}

/** Avstand fra LED-underkant til plantetopp, separat per kasse på denne enheten. */
export const lysAvstander = writable<Record<string, number>>(lesAvstander());
lysAvstander.subscribe((v) => {
  try { localStorage.setItem(AVSTAND_KEY, JSON.stringify(v)); } catch { /* privat modus */ }
});
export function settLysAvstand(potteId: string, cm: number): boolean {
  if (!Number.isFinite(cm) || cm < 10 || cm > 20) return false;
  lysAvstander.update((v) => ({ ...v, [potteId]: cm }));
  return true;
}
